<template>
  <div class="row justify-content-center">
    <!-- Header -->
    <div class="col-12 col-xl-8 mb-4">
      <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center">
        <div>
          <h3 class="page-title text-white font-weight-bold mb-1">
            <i class="mdi mdi-bank-transfer text-warning mr-1"></i>
            {{ $t('billing.confirmTransfer') }}
          </h3>
          <p class="text-muted small mb-0">{{ $t('billing.transferNotice') }}</p>
        </div>
        <div class="mt-3 mt-md-0">
          <button @click="back" class="btn btn-outline-secondary btn-sm">
            <i class="mdi mdi-arrow-left mr-1"></i>{{ $t('common.back') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Confirmation Form Card -->
    <div class="col-12 col-xl-8 grid-margin">
      <div class="card border-0 shadow-sm" style="background: #191c24; border: 1px solid #2c2e33; border-radius: 14px;">
        <div class="card-body p-4 p-md-5">
          <!-- Official Destination Bank Account Box -->
          <div class="p-3 mb-4 rounded-lg" style="background: #12151e; border: 1px solid #2c2e33;">
            <div class="d-flex align-items-center mb-2">
              <div class="p-2 rounded mr-3" style="background: rgba(245, 158, 11, 0.12); border: 1px solid rgba(245, 158, 11, 0.25);">
                <i class="mdi mdi-bank text-warning" style="font-size: 20px;"></i>
              </div>
              <div>
                <span class="text-muted small font-weight-bold text-uppercase d-block">{{ $t('billing.paymentDestination') }}</span>
                <span class="text-white font-weight-bold">Bank Central Asia (BCA) — PT Gamiteam Digital Nusantara</span>
              </div>
            </div>
            <div class="d-flex justify-content-between align-items-center pt-2 border-top" style="border-color: #2c2e33;">
              <span class="text-muted small">{{ $t('billing.accountNumber') }}:</span>
              <span class="text-warning font-monospace font-weight-bold h6 mb-0" style="letter-spacing: 1px;">
                5892 000 000
              </span>
            </div>
          </div>

          <form @submit.prevent="paymanual">
            <div class="row">
              <!-- Invoice Number & Amount -->
              <div class="col-md-6 mb-3">
                <label class="form-label text-light small font-weight-medium">
                  {{ $t('superadmin.invoiceNumber') }}
                </label>
                <input
                  type="text"
                  class="form-control text-white font-monospace"
                  style="background: #2a3038; border-color: #2c2e33;"
                  :value="invoice || 'GT-INVOICE'"
                  readonly
                />
              </div>

              <div class="col-md-6 mb-3">
                <label class="form-label text-light small font-weight-medium">
                  {{ $t('superadmin.amount') }} (IDR)
                </label>
                <input
                  type="text"
                  class="form-control text-warning font-weight-bold"
                  style="background: #2a3038; border-color: #2c2e33;"
                  :value="'Rp ' + formatPrice(amount_payment_proof)"
                  readonly
                />
              </div>

              <!-- Payment Date -->
              <div class="col-md-6 mb-3">
                <label for="paymentDate" class="form-label text-light small font-weight-medium">
                  {{ $t('workspace.payDate') }} <span class="text-danger">*</span>
                </label>
                <input
                  type="date"
                  class="form-control text-white"
                  style="background: #2a3038; border-color: #2c2e33;"
                  id="paymentDate"
                  v-model="payment_date"
                  required
                />
              </div>

              <!-- Sending Bank -->
              <div class="col-md-6 mb-3">
                <label for="senderBank" class="form-label text-light small font-weight-medium">
                  {{ $t('billing.senderBank') }} <span class="text-danger">*</span>
                </label>
                <input
                  type="text"
                  class="form-control text-white"
                  style="background: #2a3038; border-color: #2c2e33;"
                  id="senderBank"
                  :placeholder="$t('billing.bankPlaceholder') || 'e.g. BCA, Bank Mandiri, BRI, BNI'"
                  v-model="bank"
                  required
                />
              </div>

              <!-- Sender Account Name -->
              <div class="col-12 mb-3">
                <label for="senderAccountName" class="form-label text-light small font-weight-medium">
                  {{ $t('billing.senderName') }} <span class="text-danger">*</span>
                </label>
                <input
                  type="text"
                  class="form-control text-white"
                  style="background: #2a3038; border-color: #2c2e33;"
                  id="senderAccountName"
                  :placeholder="$t('billing.senderPlaceholder') || 'e.g. PT Maju Bersama / Budi Rahardjo'"
                  v-model="bank_account_name"
                  required
                />
              </div>

              <!-- Upload Payment Proof -->
              <div class="col-12 mb-4">
                <label for="paymentProofFile" class="form-label text-light small font-weight-medium">
                  {{ $t('billing.uploadProof') }} (JPG / PNG / PDF) <span class="text-danger">*</span>
                </label>
                <div class="p-3 rounded text-center" style="background: #12151e; border: 1px dashed #4b5262;">
                  <i class="mdi mdi-cloud-upload text-warning" style="font-size: 32px;"></i>
                  <input
                    type="file"
                    class="form-control mt-2"
                    style="background: #2a3038; border-color: #2c2e33; color: #fff;"
                    id="paymentProofFile"
                    @change="handleFileUpload"
                    accept="image/*,application/pdf"
                    required
                  />
                  <small class="text-muted d-block mt-1">
                    {{ $t('billing.receiptNotice') || 'Upload official transfer receipt from Mobile Banking, ATM, or Internet Banking.' }}
                  </small>
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="d-flex justify-content-end align-items-center gap-2 pt-3 border-top" style="border-color: #2c2e33;">
              <button
                type="button"
                @click="back"
                class="btn btn-outline-secondary px-4 py-2 mr-2"
                :disabled="loading"
              >
                <i class="mdi mdi-arrow-left mr-1"></i> {{ $t('common.back') }}
              </button>
              <button
                type="submit"
                class="btn btn-warning px-5 py-2 font-weight-bold text-dark"
                :disabled="loading"
              >
                <div
                  class="spinner-border spinner-border-sm text-dark mr-2"
                  role="status"
                  v-if="loading"
                >
                  <span class="visually-hidden">{{ $t('common.loading') }}</span>
                </div>
                <i class="mdi mdi-check-circle-outline mr-1" v-if="!loading"></i>
                {{ $t('billing.confirmTransfer') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import config from "../config/config";
import store from "../store";
import Swal from "sweetalert2";
import axios from "axios";

export default {
  name: "PayDetail",
  data() {
    return {
      invoice: sessionStorage.getItem("noinvoice") || "",
      amount_payment_proof: sessionStorage.getItem("amount") || 0,
      payment_date: moment().format("YYYY-MM-DD"),
      payment_proof: null,
      bank: "",
      bank_account_name: "",
      billing_id: sessionStorage.getItem("billing_id") || 0,
      loading: false,
    };
  },
  methods: {
    handleFileUpload(event) {
      if (event.target.files && event.target.files.length > 0) {
        this.payment_proof = event.target.files[0];
      }
    },
    formatPrice(value) {
      const val = (Number(value) || 0).toFixed(0);
      return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    },
    paymanual() {
      if (!this.payment_proof) {
        Swal.fire({
          icon: "warning",
          title: this.$t("billing.proofRequired") || (this.$currentLocale === "id" ? "Bukti Diperlukan" : "Proof Required"),
          text: this.$t("billing.proofRequiredNotice") || (this.$currentLocale === "id" ? "Silakan lampirkan bukti transfer pembayaran bank Anda." : "Please attach your bank transfer receipt proof."),
          confirmButtonColor: "#f59e0b",
        });
        return;
      }

      this.loading = true;
      const API_URL = config.API_URL;
      const token = store.state.authToken;

      const formData = new FormData();
      formData.append("billing_id", this.billing_id);
      formData.append("payment_proof", this.payment_proof);
      formData.append("payment_date", this.payment_date);
      formData.append("amount_payment_proof", this.amount_payment_proof);
      formData.append("bank", this.bank);
      formData.append("bank_account_name", this.bank_account_name);
      formData.append("type", "manual");
      formData.append("_method", "POST");

      axios
        .post(`${API_URL}billing/confirm/payment-manual`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        })
        .then(() => {
          Swal.fire({
            icon: "success",
            title: this.$t("billing.paymentSubmitted") || (this.$currentLocale === "id" ? "Pembayaran Berhasil Dikirim!" : "Payment Submitted!"),
            text: this.$t("billing.paymentSubmittedDesc") || (this.$currentLocale === "id" ? "Bukti transfer manual Anda telah dikirim untuk diverifikasi. Superadmin akan segera mengaktifkan kuota Anda." : "Your manual payment proof has been submitted for verification. Superadmin will activate your quota shortly."),
            confirmButtonColor: "#f59e0b",
          }).then(() => {
            this.$router.push({ name: "Billing" });
          });
        })
        .catch((err) => {
          const errMsg = err.response?.data?.message || (this.$currentLocale === "id" ? "Gagal mengonfirmasi pembayaran manual." : "Failed to confirm manual payment.");
          Swal.fire({
            icon: "error",
            title: this.$t("billing.submissionError") || (this.$currentLocale === "id" ? "Gagal Mengirimkan" : "Submission Error"),
            text: errMsg,
            confirmButtonColor: "#f59e0b",
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    back() {
      this.$router.go(-1);
    },
  },
};
</script>

<style scoped>
.form-control:focus {
  border-color: #f59e0b !important;
  box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.15) !important;
}
</style>
